import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { FormArray, FormControl, Validators } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { TranslateService } from '@ngx-translate/core'
import moment from 'moment'
import { firstValueFrom, interval, Subscription, switchMap } from 'rxjs'
import { LabelToId } from 'src/app/common/input-utils'
import { SubmitNewPlanConfirmModalComponent } from 'src/app/components/complex/modals/submit-new-plan-confirm-modal/submit-new-plan-confirm-modal.component'
import { ClientDto } from 'src/app/models/client/client-dto'
import { ServicePlanFeeBreakdownDto } from 'src/app/models/service-plan/service-plan-fee-breakdown-dto'
import {
  getLineItemFee,
  SERVICE_PLAN_FEE_KEYS,
} from 'src/app/models/service-plan/service-plan-fee-keys'
import {
  ServicePlanFieldError,
  ServicePlanStepComponent,
} from 'src/app/models/service-plan/service-plan-field-error'
import {
  SERVICE_PLAN_SCHEMA_VERSION,
  ServicePlanPayload,
} from 'src/app/models/service-plan/service-plan-payload'
import { ServicePlanStatusDto } from 'src/app/models/service-plan/service-plan-status-dto'
import { BannerService } from 'src/app/services/banner/banner.service'
import { ClientEmailService } from 'src/app/services/client-email/client-email.service'
import { ServicePlanFeeService } from 'src/app/services/service-plan/service-plan-fee.service'
import { ServicePlanPreviewService } from 'src/app/services/service-plan/service-plan-preview.service'
import { ServicePlanStateService } from 'src/app/services/service-plan/service-plan-state.service'
import { ServicePlanSubmissionService } from 'src/app/services/service-plan/service-plan-submission.service'
import { SpinnerService } from 'src/app/services/spinner/spinner.service'

// ServicePlanRequestValidator.ValidateTemplateVersion's exact message (tsc.common) always contains
// this substring — matched here rather than the whole message, since the message also embeds the
// two version numbers.
const TEMPLATE_VERSION_MISMATCH_SUBSTRING =
  'no longer matches the current template version'

// Code review (e9-1): a cap on additional recipients — primary + 5 additional (6 total) — matches a
// typical immediate-family-plus-clinician recipient count; the "+ Add additional recipient"
// affordance is hidden once reached.
const MAX_ADDITIONAL_RECIPIENTS = 5

// Story e9-2 (Task 4) — a conservative placeholder, not a researched number (same flagged-placeholder
// precedent as e4-1's own retry/backoff numbers): 3s between polls, capped at 40 polls (~2 minutes) so
// an unexpectedly slow pipeline doesn't poll forever on an open tab.
const POLL_INTERVAL_MS = 3000
const MAX_POLL_COUNT = 40

type OutcomeRowStatus = 'pending' | 'done' | 'failed'

interface OutcomeRowViewModel {
  labelKey: string
  status: OutcomeRowStatus
}

const PLAN_TYPE_LABEL_KEYS: Record<string, string> = {
  transition: 'SERVICE_PLAN.STEP.PLAN_TYPE.OPTION_TRANSITION',
  gsp: 'SERVICE_PLAN.STEP.PLAN_TYPE.OPTION_GSP',
  first: 'SERVICE_PLAN.STEP.PLAN_TYPE.OPTION_FIRST',
}

interface FeeRowConfig {
  labelKey: string
  feeKey: string
  // Only the 5 quantity-based Additional Services rows have this — the 5 toggle-based rows and the
  // 4 fixed/block rows render no "N × $unit" note (ServicePlanSectionConfigProvider's own QuantityPath
  // vs TogglePath split).
  quantityOf?: (payload: ServicePlanPayload) => number
  // Only the ten additionalServices.* rows and the CFSS/Audiology block rows ever show "Not required
  // this year" (rule 12) — Annual Support Package and the hearing-language row are never $0.
  isOptional: boolean
}

interface FeeRowViewModel {
  labelKey: string
  amountDisplay: string
  noteDisplay: string | null
  isZero: boolean
}

// Row order/labels/keys copied verbatim from ServicePlanSectionConfigProvider.cs's
// SummaryOfSupportFees/AdditionalSupportServices descriptors (tsc.common) — the review screen and
// the PDF must never disagree on what rows exist (Dev Notes). Exported (as FEE_ROW_KEYS_IN_ORDER
// below) solely so a frontend parity test can pin this exact order/key-set and fail loudly if either
// side drifts — see review-send-step.component.spec.ts's 'fee-row parity' describe block.
const FEE_ROW_CONFIGS: FeeRowConfig[] = [
  {
    labelKey: 'SERVICE_PLAN.STEP.REVIEW.FEE.ANNUAL_SUPPORT_PACKAGE',
    feeKey: SERVICE_PLAN_FEE_KEYS.AnnualSupportPackage,
    isOptional: false,
  },
  {
    labelKey: 'SERVICE_PLAN.STEP.REVIEW.FEE.HEARING_LANGUAGE_SUPPORT',
    feeKey: SERVICE_PLAN_FEE_KEYS.HearingLanguageFee,
    isOptional: false,
  },
  {
    labelKey: 'SERVICE_PLAN.STEP.REVIEW.FEE.COMMUNITY_VISITS',
    feeKey: SERVICE_PLAN_FEE_KEYS.CommunityVisits,
    isOptional: true,
    quantityOf: (p) => p.additionalServices.communityVisitsQty,
  },
  {
    labelKey: 'SERVICE_PLAN.STEP.REVIEW.FEE.TRAVEL_TO_YOU',
    feeKey: SERVICE_PLAN_FEE_KEYS.TravelToYou,
    isOptional: true,
    quantityOf: (p) => p.additionalServices.travelQty,
  },
  {
    labelKey: 'SERVICE_PLAN.STEP.REVIEW.FEE.COLLABORATIVE_CARE_CFSS',
    feeKey: SERVICE_PLAN_FEE_KEYS.CollaborativeCareCfss,
    isOptional: true,
    quantityOf: (p) => p.additionalServices.collaborativeCareCfssQty,
  },
  {
    labelKey: 'SERVICE_PLAN.STEP.REVIEW.FEE.COLLABORATIVE_CARE_AUD',
    feeKey: SERVICE_PLAN_FEE_KEYS.CollaborativeCareAud,
    isOptional: true,
    quantityOf: (p) => p.additionalServices.collaborativeCareAudQty,
  },
  {
    labelKey:
      'SERVICE_PLAN.STEP.REVIEW.FEE.ADDITIONAL_COMMUNICATION_ASSESSMENT',
    feeKey: SERVICE_PLAN_FEE_KEYS.AdditionalCommunicationAssessment,
    isOptional: true,
    quantityOf: (p) =>
      p.additionalServices.additionalCommunicationAssessmentQty,
  },
  {
    labelKey: 'SERVICE_PLAN.STEP.REVIEW.FEE.STARTING_SCHOOL_REPORT',
    feeKey: SERVICE_PLAN_FEE_KEYS.StartingSchoolReport,
    isOptional: true,
  },
  {
    labelKey: 'SERVICE_PLAN.STEP.REVIEW.FEE.SOCIAL_EMOTIONAL_ASSESSMENT',
    feeKey: SERVICE_PLAN_FEE_KEYS.SocialEmotionalAssessment,
    isOptional: true,
  },
  {
    labelKey: 'SERVICE_PLAN.STEP.REVIEW.FEE.SPEECH_PERCEPTION_TESTING',
    feeKey: SERVICE_PLAN_FEE_KEYS.SpeechPerceptionTesting,
    isOptional: true,
  },
  {
    labelKey: 'SERVICE_PLAN.STEP.REVIEW.FEE.ANNUAL_SPEECH_PERCEPTION',
    feeKey: SERVICE_PLAN_FEE_KEYS.AnnualSpeechPerception,
    isOptional: true,
  },
  {
    labelKey: 'SERVICE_PLAN.STEP.REVIEW.FEE.CFSS_TOTAL',
    feeKey: SERVICE_PLAN_FEE_KEYS.CfssTotal,
    isOptional: true,
  },
  {
    labelKey: 'SERVICE_PLAN.STEP.REVIEW.FEE.AUDIOLOGY_TOTAL',
    feeKey: SERVICE_PLAN_FEE_KEYS.AudiologyTotal,
    isOptional: true,
  },
]

// Parity-test surface only — see the comment above FEE_ROW_CONFIGS.
export const FEE_ROW_KEYS_IN_ORDER: string[] = FEE_ROW_CONFIGS.map(
  (row) => row.feeKey,
)

// Step 11 — Plan Summary, PDF Preview & Recipient Confirmation (e9-1) plus Send Action & Delivery
// Outcome (e9-2). Has no Continue button (Task 5 suppresses it for this step) — Send's enabled
// state is this component's own isSendEnabled getter, read directly by its own template, not the
// shell's shared isLiveValid/isContinueDisabled mechanism.
@Component({
  selector: 'app-service-plan-review-send-step',
  templateUrl: './review-send-step.component.html',
  styleUrls: ['./review-send-step.component.scss'],
  standalone: false,
})
export class ReviewSendStepComponent
  implements OnInit, OnDestroy, ServicePlanStepComponent
{
  // Passed by the shell alongside clientInformationAttributes — same ClientDto the shell already
  // loaded, including the NDIS number NdisPlanStepComponent's own prefill already resolved (AC1 —
  // never re-call NdisPlanService a second time for the same client).
  @Input() clientId = ''
  @Input() clientDetails?: ClientDto

  introText = ''
  participantCardLabel = ''
  participantNameLabel = ''
  participantDobLabel = ''
  participantNdisNumberLabel = ''
  planCardLabel = ''
  planTypeLabel = ''
  planStartLabel = ''
  planEndLabel = ''
  goalsCardLabel = ''
  feeHeading = ''
  totalFeeLabel = ''
  generatePdfLabel = ''
  pdfGeneratedLabel = ''
  sendEmailLabel = ''
  sentButtonLabel = ''
  primaryEmailLabel = ''
  addRecipientLabel = ''
  removeRecipientLabel = ''
  accessibleSummaryHeading = ''
  pdfPreviewTitle = ''

  primaryEmailControl!: FormControl<string | null>
  additionalEmailsArray = new FormArray<FormControl<string | null>>([])
  isPrimaryEmailPrefilled = false
  primaryEmailControlId = ''

  feeBreakdown: ServicePlanFeeBreakdownDto | null = null
  isFeeSummaryLoading = false

  isPreviewGenerated = false
  pdfBlobUrl: string | null = null

  // Send Action & Delivery Outcome (e9-2)
  isSending = false
  isSendComplete = false
  submissionId: string | null = null
  status: ServicePlanStatusDto | null = null
  isPollingCapped = false
  // Code review (2026-10-04) — in-flight guards preventing double-clicks/concurrent actions from
  // racing startPolling()'s submissionId or re-triggering a call already underway.
  isRetrying = false
  isCheckingAgain = false

  private pdfObjectUrl: string | null = null
  private previewTemplateVersion: number | null = null
  private readonly subscription = new Subscription()
  private pollSubscription?: Subscription
  private pollCount = 0

  constructor(
    private state: ServicePlanStateService,
    private translate: TranslateService,
    private feeService: ServicePlanFeeService,
    private previewService: ServicePlanPreviewService,
    private submissionService: ServicePlanSubmissionService,
    private clientEmailService: ClientEmailService,
    private bannerService: BannerService,
    private spinnerService: SpinnerService,
    private dialog: MatDialog,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const t = (key: string) => this.translate.instant(key)

    this.introText = t('SERVICE_PLAN.STEP.REVIEW.INTRO')
    this.participantCardLabel = t(
      'SERVICE_PLAN.STEP.REVIEW.PARTICIPANT_CARD_LABEL',
    )
    this.participantNameLabel = t(
      'SERVICE_PLAN.STEP.REVIEW.PARTICIPANT_NAME_LABEL',
    )
    this.participantDobLabel = t(
      'SERVICE_PLAN.STEP.REVIEW.PARTICIPANT_DOB_LABEL',
    )
    this.participantNdisNumberLabel = t(
      'SERVICE_PLAN.STEP.REVIEW.PARTICIPANT_NDIS_NUMBER_LABEL',
    )
    this.planCardLabel = t('SERVICE_PLAN.STEP.REVIEW.PLAN_CARD_LABEL')
    this.planTypeLabel = t('SERVICE_PLAN.STEP.REVIEW.PLAN_TYPE_LABEL')
    this.planStartLabel = t('SERVICE_PLAN.STEP.REVIEW.PLAN_START_LABEL')
    this.planEndLabel = t('SERVICE_PLAN.STEP.REVIEW.PLAN_END_LABEL')
    this.goalsCardLabel = t('SERVICE_PLAN.STEP.REVIEW.GOALS_CARD_LABEL')
    this.feeHeading = t('SERVICE_PLAN.STEP.REVIEW.FEE_HEADING')
    this.totalFeeLabel = t('SERVICE_PLAN.STEP.REVIEW.FEE.TOTAL_FEE')
    this.generatePdfLabel = t('SERVICE_PLAN.STEP.REVIEW.GENERATE_PDF_BUTTON')
    this.pdfGeneratedLabel = t('SERVICE_PLAN.STEP.REVIEW.PDF_GENERATED_BUTTON')
    this.sendEmailLabel = t('SERVICE_PLAN.STEP.REVIEW.SEND_EMAIL_BUTTON')
    this.sentButtonLabel = t('SERVICE_PLAN.STEP.REVIEW.SENT_BUTTON')
    this.primaryEmailLabel = t('SERVICE_PLAN.STEP.REVIEW.PRIMARY_EMAIL_LABEL')
    this.addRecipientLabel = t('SERVICE_PLAN.STEP.REVIEW.ADD_RECIPIENT_LABEL')
    this.removeRecipientLabel = t(
      'SERVICE_PLAN.STEP.REVIEW.REMOVE_RECIPIENT_LABEL',
    )
    this.accessibleSummaryHeading = t(
      'SERVICE_PLAN.STEP.REVIEW.ACCESSIBLE_SUMMARY_HEADING',
    )
    this.pdfPreviewTitle = t('SERVICE_PLAN.STEP.REVIEW.PDF_PREVIEW_TITLE')

    this.primaryEmailControlId = LabelToId(this.primaryEmailLabel)

    const payload = this.state.getPayload()

    const prefillPrimary = payload.recipients.primaryEmail
    this.primaryEmailControl = new FormControl<string | null>(prefillPrimary, {
      validators: [Validators.required, Validators.email],
    })

    for (const email of payload.recipients.additionalEmails) {
      this.additionalEmailsArray.push(this.buildAdditionalEmailControl(email))
    }

    if (!prefillPrimary && this.clientId) {
      this.subscription.add(
        this.clientEmailService.GetEmail(this.clientId).subscribe({
          next: (result) => {
            if (result.email && !this.primaryEmailControl.value) {
              this.primaryEmailControl.setValue(result.email)
              this.isPrimaryEmailPrefilled = true
            }
            this.cd.markForCheck()
          },
          error: () => {
            // Degrade silently (AC5) — the field stays blank, the clinician can always type one in.
          },
        }),
      )
    }

    this.subscription.add(
      this.primaryEmailControl.valueChanges.subscribe(() => {
        this.recalculateDuplicates()
        this.writeRecipients()
      }),
    )
    this.subscription.add(
      this.additionalEmailsArray.valueChanges.subscribe(() => {
        this.recalculateDuplicates()
        this.writeRecipients()
      }),
    )

    this.loadFeeSummary()
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe()
    this.pollSubscription?.unsubscribe()
    this.revokePdfBlobUrl()
  }

  // ServicePlanStepComponent contract. There is no Continue button on this step (Task 5), so the
  // shell never calls these — implemented anyway so the component honestly satisfies its contract.
  isValid(): boolean {
    this.primaryEmailControl.markAsTouched()
    this.additionalEmailsArray.controls.forEach((control) =>
      control.markAsTouched(),
    )
    return this.isSendEnabled
  }

  getValidationErrors(): ServicePlanFieldError[] {
    const errors: ServicePlanFieldError[] = []
    if (!this.primaryEmailControl.valid) {
      errors.push({
        controlId: this.primaryEmailControlId,
        messageKey: this.primaryEmailErrorKey,
      })
    }
    this.additionalEmailsArray.controls.forEach((control, index) => {
      if (!control.valid) {
        errors.push({
          controlId: this.additionalEmailControlId(index),
          messageKey: this.additionalEmailErrorKey(control),
        })
      }
    })
    return errors
  }

  // Rule 13 (AC6) — read directly by this component's own template for the Send button's
  // [isDisabled] binding, not the shell's shared isLiveValid/isContinueDisabled (Dev Notes).
  // Code review (e9-1): also requires a successfully generated PDF preview to exist — Send must
  // never be enabled on top of a never-generated or now-stale (post-failure) preview.
  get isSendEnabled(): boolean {
    if (!this.primaryEmailControl) {
      return false
    }
    return (
      this.isPreviewGenerated &&
      this.primaryEmailControl.valid &&
      this.additionalEmailsArray.controls.every((control) => control.valid)
    )
  }

  get isAtRecipientLimit(): boolean {
    return this.additionalEmailsArray.length >= MAX_ADDITIONAL_RECIPIENTS
  }

  // AC10 — disabled for the duration of the in-flight request, and permanently once sent (the
  // button swaps to kind="success" at that point, never re-enabled by a later form edit).
  get isSendButtonDisabled(): boolean {
    return this.isSending || this.isSendComplete || !this.isSendEnabled
  }

  // AC1 (revised per Lee's direct copy feedback — the static "Finalise and send" replaces the
  // originally-specced per-recipient naming): three states — empty/no valid primary email (rule
  // 13's disabled state, reuses the already-shipped static SEND_EMAIL_BUTTON key, no new key
  // needed), ready-to-send (SEND_BUTTON_LABEL key, now a fixed "Finalise and send" string), sent
  // (reuses the already-shipped SENT_BUTTON key).
  get sendButtonLabel(): string {
    if (this.isSendComplete) {
      return this.sentButtonLabel
    }
    if (this.primaryEmailControl?.valid && this.primaryEmailControl.value) {
      return this.translate.instant(
        'SERVICE_PLAN.STEP.REVIEW.SEND_BUTTON_LABEL',
      )
    }
    return this.sendEmailLabel
  }

  // Feeds the already-shipped SENT_LINE post-send reminder ("Sent to {{allRecipientsDisplay}} —
  // a copy is filed against the client record.") — comma-joined, skipping any empty/invalid entry.
  get allRecipientsDisplay(): string {
    const emails: string[] = []
    if (this.primaryEmailControl?.valid && this.primaryEmailControl.value) {
      emails.push(this.primaryEmailControl.value)
    }
    for (const control of this.additionalEmailsArray.controls) {
      if (control.valid && control.value) {
        emails.push(control.value)
      }
    }
    return emails.join(', ')
  }

  // AC3 — four status rows (record saved is always true/shown immediately on success, since
  // submittedOn is set at record creation). pdfGeneratedOn is tracked on the status DTO but is not
  // one of the four rows the AC names — a pipeline-internal step, not its own clinician-facing outcome.
  get outcomeRows(): OutcomeRowViewModel[] {
    if (!this.status) {
      return []
    }
    const failed = !!this.status.failureReason
    return [
      {
        labelKey: 'SERVICE_PLAN.STEP.REVIEW.OUTCOME_RECORD_SAVED',
        status: 'done',
      },
      {
        labelKey: 'SERVICE_PLAN.STEP.REVIEW.OUTCOME_EMAIL_SENT',
        status: this.outcomeRowStatus(this.status.emailSentOn, failed),
      },
      {
        labelKey: 'SERVICE_PLAN.STEP.REVIEW.OUTCOME_PRACSUITE_ATTACHMENT',
        status: this.outcomeRowStatus(this.status.pdfAttachedOn, failed),
      },
      {
        labelKey: 'SERVICE_PLAN.STEP.REVIEW.OUTCOME_PRACSUITE_NOTE',
        status: this.outcomeRowStatus(this.status.noteCreatedOn, failed),
      },
    ]
  }

  // Never colour alone (pre-review-checklist.md) — paired with outcomeRows' own icon glyph in the
  // template, both driven by this same status value.
  outcomeStatusLabelKey(status: OutcomeRowStatus): string {
    switch (status) {
      case 'done':
        return 'SERVICE_PLAN.STEP.REVIEW.OUTCOME_STATUS_DONE'
      case 'failed':
        return 'SERVICE_PLAN.STEP.REVIEW.OUTCOME_STATUS_FAILED'
      default:
        return 'SERVICE_PLAN.STEP.REVIEW.OUTCOME_STATUS_PENDING'
    }
  }

  get primaryEmailErrorText(): string {
    const key = this.primaryEmailErrorKey
    return key ? this.translate.instant(key) : ''
  }

  additionalEmailErrorText(control: FormControl<string | null>): string {
    const key = this.additionalEmailErrorKey(control)
    return key ? this.translate.instant(key) : ''
  }

  addRecipient(): void {
    if (this.isAtRecipientLimit) {
      return
    }
    this.additionalEmailsArray.push(this.buildAdditionalEmailControl(null))
  }

  // Tracks by control identity, not index — removing a control from the middle of the array must not
  // cause Angular to reuse/relabel a surviving control's DOM node under a shifted index.
  trackByControl(
    _index: number,
    control: FormControl<string | null>,
  ): FormControl<string | null> {
    return control
  }

  removeRecipient(index: number): void {
    this.additionalEmailsArray.removeAt(index)
    this.recalculateDuplicates()
    this.writeRecipients()
  }

  // AC1, AC2, AC6, AC10 — builds the submit request from the current in-memory payload/recipients
  // and the last successful preview's template version, then starts the outcome poll on success.
  onSend(): void {
    if (this.isSendButtonDisabled) {
      return
    }

    this.isSending = true
    const recipientEmails = this.recipientEmails()

    this.subscription.add(
      this.submissionService
        .Submit({
          clientId: this.clientId,
          schemaVersion: SERVICE_PLAN_SCHEMA_VERSION,
          payload: this.state.getPayload(),
          templateVersion: this.previewTemplateVersion,
          recipientEmails,
        })
        .subscribe({
          next: (result) => {
            this.isSending = false
            this.isSendComplete = true
            this.submissionId = result.submissionId
            this.status = {
              submissionId: result.submissionId,
              submittedOn: result.submittedOn,
              pdfGeneratedOn: null,
              emailSentOn: null,
              pdfAttachedOn: null,
              noteCreatedOn: null,
              failureReason: null,
            }
            this.state.markSubmissionCreated()
            this.startPolling()
            // ReviewSendStepComponent is nested under ServicePlanStepperComponent's OnPush view —
            // without this, Angular's CD walk never revisits this component for an update arriving
            // this long after the Send click's own (already-consumed) dirty-marking pass.
            this.cd.markForCheck()
          },
          error: (err: HttpErrorResponse) => {
            // AC6 — the in-memory payload/form is left completely untouched on failure; no
            // state.updatePayload()/reset()/clear() call on this path.
            this.isSending = false
            this.handleSubmitError(err)
            this.cd.markForCheck()
          },
        }),
    )
  }

  // AC4 — re-drives only the failed step(s) on the same submission record; never a resubmit, never
  // a new record. Clears the previous failure display while the retry is in flight (Task 5) so a
  // stale failure message doesn't sit alongside a fresh attempt. Code review (2026-10-04): guarded
  // against double-clicks (isRetrying), and a failed Retry call restores the previous failureReason
  // instead of leaving it permanently cleared — otherwise a still-failed submission would render as
  // merely pending, with the Retry button gone and no way to try again short of a reload.
  retry(): void {
    if (!this.submissionId || !this.status || this.isRetrying) {
      return
    }
    const previousFailureReason = this.status.failureReason
    this.isRetrying = true
    this.status = { ...this.status, failureReason: null }
    this.isPollingCapped = false

    this.subscription.add(
      this.submissionService.Retry(this.submissionId).subscribe({
        next: () => {
          this.isRetrying = false
          this.startPolling()
          this.cd.markForCheck()
        },
        error: () => {
          this.isRetrying = false
          this.status = this.status
            ? { ...this.status, failureReason: previousFailureReason }
            : this.status
          this.bannerService.showBanner(
            this.translate.instant(
              'SERVICE_PLAN.STEP.REVIEW.SUBMIT_ERROR_BANNER',
            ),
            'error',
            30000,
          )
          this.cd.markForCheck()
        },
      }),
    )
  }

  // AC5 — a separate, explicitly-labelled action, never conflated with Retry: re-invokes the same
  // submit flow against the still-intact in-memory payload, producing a brand-new submission record.
  // Code review (2026-10-04): a successful send must never be re-sent by a stray click — this now
  // requires explicit confirmation (a second email + a second PracSuite attachment/note are real
  // consequences, not a harmless no-op) and guards re-entrancy while that confirmation is pending.
  async submitNewPlan(): Promise<void> {
    if (this.isSending) {
      return
    }
    this.isSending = true

    const dialogRef = this.dialog.open(SubmitNewPlanConfirmModalComponent, {
      width: '605px',
      panelClass: 'are-you-sure-panel',
      disableClose: true,
    })
    const confirmed = await firstValueFrom(dialogRef.afterClosed())

    if (!confirmed) {
      this.isSending = false
      return
    }

    this.pollSubscription?.unsubscribe()
    this.submissionId = null
    this.status = null
    this.isPollingCapped = false
    this.isSendComplete = false
    this.isSending = false
    this.cd.markForCheck()
    this.onSend()
  }

  // Task 4 — the safety-cap's manual affordance: a single one-shot status read, not a restart of
  // the interval poll. Code review (2026-10-04): guarded against double-clicks (isCheckingAgain) and
  // given an error handler — a failed one-shot check previously failed completely silently.
  checkAgain(): void {
    if (!this.submissionId || this.isCheckingAgain) {
      return
    }
    this.isCheckingAgain = true
    this.subscription.add(
      this.submissionService.GetStatus(this.submissionId).subscribe({
        next: (status) => {
          this.isCheckingAgain = false
          this.status = status
          if (status.failureReason || this.isOutcomeFullyComplete(status)) {
            this.isPollingCapped = false
          }
          this.cd.markForCheck()
        },
        error: () => {
          this.isCheckingAgain = false
          this.bannerService.showBanner(
            this.translate.instant(
              'SERVICE_PLAN.STEP.REVIEW.SUBMIT_ERROR_BANNER',
            ),
            'error',
            30000,
          )
          this.cd.markForCheck()
        },
      }),
    )
  }

  // Code review (2026-10-04) — trackBy for outcomeRows' *ngFor; rows are a fixed, stable set keyed
  // by their translation key, never reordered/added/removed at runtime.
  trackByOutcomeRow(_index: number, row: OutcomeRowViewModel): string {
    return row.labelKey
  }

  // Participant / Plan / Goals summary cards (AC1) — read directly from payload + clientDetails,
  // no new data-entry fields on this step.
  get participantName(): string {
    return this.clientDetails?.preferredFullName ?? ''
  }

  get participantDob(): string {
    return this.clientDetails?.dateOfBirth
      ? moment(this.clientDetails.dateOfBirth).format('DD/MM/YYYY')
      : ''
  }

  get participantNdisNumber(): string {
    return this.clientDetails?.ndisNumber ?? ''
  }

  get planTypeDisplay(): string {
    const planType = this.state.getPayload().planType
    const key = planType ? PLAN_TYPE_LABEL_KEYS[planType] : undefined
    return key ? this.translate.instant(key) : ''
  }

  get planStartDisplay(): string {
    const value = this.state.getPayload().ndis.startDate
    return value ? moment(value).format('DD/MM/YYYY') : ''
  }

  get planEndDisplay(): string {
    const value = this.state.getPayload().ndis.endDate
    return value ? moment(value).format('DD/MM/YYYY') : ''
  }

  // Resolved during story creation — build an independent Goals card, visible only for the same
  // plan types FamilyGoalsStepComponent's own step is visible for, and only when populated (no
  // meaningful "none selected" copy to show, unlike a $0 fee row).
  get showGoalsCard(): boolean {
    const payload = this.state.getPayload()
    return (
      (payload.planType === 'gsp' || payload.planType === 'first') &&
      !!payload.familyGoals
    )
  }

  get familyGoalsText(): string {
    return this.state.getPayload().familyGoals ?? ''
  }

  // Fee breakdown (AC1, AC2) — computed server-side via the already-built fee calculator; this
  // getter only maps the DTO's totals into display rows against the frontend-owned row list.
  get feeRows(): FeeRowViewModel[] {
    if (!this.feeBreakdown) {
      return []
    }
    const payload = this.state.getPayload()
    return FEE_ROW_CONFIGS.map((config) => this.buildFeeRow(config, payload))
  }

  get totalFeeDisplay(): string {
    return this.feeBreakdown
      ? this.formatCurrency(this.feeBreakdown.totalFee)
      : ''
  }

  generatePdf(): void {
    this.spinnerService.show()
    this.subscription.add(
      this.previewService
        .GeneratePreview(
          this.clientId,
          SERVICE_PLAN_SCHEMA_VERSION,
          this.state.getPayload(),
          this.previewTemplateVersion,
        )
        .subscribe({
          next: (result) => {
            this.revokePdfBlobUrl()
            this.pdfObjectUrl = URL.createObjectURL(result.pdfBlob)
            this.pdfBlobUrl = this.pdfObjectUrl
            this.previewTemplateVersion = result.templateVersion
            this.isPreviewGenerated = true
            this.cd.markForCheck()
          },
          error: (err: HttpErrorResponse) => {
            // Code review (e9-1, AC4): a failed regeneration must not leave the previously-generated
            // preview looking current — clear the success state/blob/template version so the button
            // reverts to "Generate PDF" and a stale template version is never retried against.
            this.revokePdfBlobUrl()
            this.pdfBlobUrl = null
            this.previewTemplateVersion = null
            this.isPreviewGenerated = false
            this.handlePreviewError(err)
            this.cd.markForCheck()
          },
          complete: () => {
            this.spinnerService.hide()
          },
        }),
    )
  }

  private get primaryEmailErrorKey(): string {
    const errors = this.primaryEmailControl?.errors
    if (!errors) {
      return ''
    }
    if (errors['required']) {
      return 'SERVICE_PLAN.VALIDATION.PRIMARY_EMAIL_REQUIRED'
    }
    if (errors['duplicate']) {
      return 'SERVICE_PLAN.VALIDATION.EMAIL_DUPLICATE'
    }
    if (errors['email']) {
      return 'SERVICE_PLAN.VALIDATION.EMAIL_FORMAT_INVALID'
    }
    return ''
  }

  private additionalEmailErrorKey(control: FormControl<string | null>): string {
    if (!control.errors) {
      return ''
    }
    if (control.errors['duplicate']) {
      return 'SERVICE_PLAN.VALIDATION.EMAIL_DUPLICATE'
    }
    if (control.errors['email']) {
      return 'SERVICE_PLAN.VALIDATION.EMAIL_FORMAT_INVALID'
    }
    return ''
  }

  private additionalEmailControlId(index: number): string {
    const label = this.translate.instant(
      'SERVICE_PLAN.STEP.REVIEW.ADDITIONAL_RECIPIENT_LABEL',
      { index: index + 1 },
    )
    return LabelToId(label)
  }

  private buildAdditionalEmailControl(
    value: string | null,
  ): FormControl<string | null> {
    return new FormControl<string | null>(value, {
      validators: [Validators.email],
    })
  }

  // AC7 — closes the exact gap e2-2 deferred to this epic: well-formed-email format (Validators.email
  // on every control) plus exact-match duplicate detection across primary + additional, normalized
  // case-insensitively (AC7's own "A@x.com"/"a@x.com" example) before the step is considered valid.
  private recalculateDuplicates(): void {
    const allControls: FormControl<string | null>[] = [
      this.primaryEmailControl,
      ...this.additionalEmailsArray.controls,
    ]
    const countsByNormalizedValue = new Map<string, number>()
    for (const control of allControls) {
      const normalized = this.normalize(control.value)
      if (!normalized) {
        continue
      }
      countsByNormalizedValue.set(
        normalized,
        (countsByNormalizedValue.get(normalized) ?? 0) + 1,
      )
    }

    for (const control of allControls) {
      const normalized = this.normalize(control.value)
      const isDuplicate = normalized
        ? (countsByNormalizedValue.get(normalized) ?? 0) > 1
        : false

      const currentErrors = { ...(control.errors ?? {}) }
      delete currentErrors['duplicate']
      if (isDuplicate) {
        currentErrors['duplicate'] = true
      }
      control.setErrors(
        Object.keys(currentErrors).length > 0 ? currentErrors : null,
      )
    }
  }

  private normalize(value: string | null): string {
    return (value ?? '').trim().toLowerCase()
  }

  // AC9 — every edit here writes straight into payload.recipients for this send only; never a
  // write-back to the client/caregiver contact record.
  private writeRecipients(): void {
    const additionalEmails = this.additionalEmailsArray.controls
      .map((control) => control.value?.trim() ?? '')
      .filter((value): value is string => !!value)

    this.state.updatePayload({
      recipients: {
        primaryEmail: this.primaryEmailControl.value?.trim() || null,
        additionalEmails,
      },
    })
  }

  private loadFeeSummary(): void {
    this.isFeeSummaryLoading = true
    this.subscription.add(
      this.feeService
        .GetFeeSummary(
          this.clientId,
          SERVICE_PLAN_SCHEMA_VERSION,
          this.state.getPayload(),
        )
        .subscribe({
          next: (result) => {
            this.feeBreakdown = result
          },
          error: () => {
            this.isFeeSummaryLoading = false
            this.bannerService.showBanner(
              this.translate.instant(
                'SERVICE_PLAN.STEP.REVIEW.FEE_SUMMARY_ERROR_BANNER',
              ),
              'error',
              30000,
            )
            this.cd.markForCheck()
          },
          complete: () => {
            this.isFeeSummaryLoading = false
            this.cd.markForCheck()
          },
        }),
    )
  }

  private buildFeeRow(
    config: FeeRowConfig,
    payload: ServicePlanPayload,
  ): FeeRowViewModel {
    const amount = getLineItemFee(
      this.feeBreakdown!.lineItemFees,
      config.feeKey,
    )
    const isZero = config.isOptional && amount === 0

    let noteDisplay: string | null = null
    if (!isZero && config.quantityOf) {
      const quantity = config.quantityOf(payload)
      if (quantity > 0) {
        const unitAmount = amount / quantity
        noteDisplay = `${quantity} × ${this.formatCurrency(unitAmount)}`
      }
    }

    return {
      labelKey: config.labelKey,
      amountDisplay: isZero
        ? this.translate.instant('SERVICE_PLAN.STEP.REVIEW.FEE.NOT_REQUIRED')
        : this.formatCurrency(amount),
      noteDisplay,
      isZero,
    }
  }

  // Deliberately not Angular's CurrencyPipe — explicit 'en-AU' keeps this deterministic regardless
  // of the app's configured LOCALE_ID, matching BaselinePackageStepComponent's own documented
  // reasoning for avoiding CurrencyPipe.
  private formatCurrency(amount: number): string {
    return amount.toLocaleString('en-AU', {
      style: 'currency',
      currency: 'AUD',
    })
  }

  private handlePreviewError(err: HttpErrorResponse): void {
    this.spinnerService.hide()
    this.resolveErrorText(err.error, (text) => {
      if (this.matchesTemplateVersionMismatch(text)) {
        this.bannerService.showBanner(
          this.translate.instant(
            'SERVICE_PLAN.STEP.REVIEW.TEMPLATE_VERSION_MISMATCH_BANNER',
          ),
          'error',
          30000,
        )
      } else {
        this.showGenericPreviewError()
      }
    })
  }

  // AC6 — Submit's 400 for a stale template version is the identical ValidateTemplateVersion
  // message Preview's own error path already detects (Dev Notes "Submit already validates template
  // version") — routed through the same matchesTemplateVersionMismatch/resolveErrorText helpers
  // rather than re-deriving the detection a second way.
  private handleSubmitError(err: HttpErrorResponse): void {
    this.resolveErrorText(err.error, (text) => {
      if (this.matchesTemplateVersionMismatch(text)) {
        this.bannerService.showBanner(
          this.translate.instant(
            'SERVICE_PLAN.STEP.REVIEW.TEMPLATE_VERSION_MISMATCH_BANNER',
          ),
          'error',
          30000,
        )
      } else {
        this.bannerService.showBanner(
          this.translate.instant(
            'SERVICE_PLAN.STEP.REVIEW.SUBMIT_ERROR_BANNER',
          ),
          'error',
          30000,
        )
      }
    })
  }

  private matchesTemplateVersionMismatch(text: string): boolean {
    return text.includes(TEMPLATE_VERSION_MISMATCH_SUBSTRING)
  }

  // Shared by the Preview (Blob-bodied, responseType: 'blob') and Submit (plain-JSON-bodied) error
  // paths — a 400's error body only needs its own async .text() read when it arrives as a Blob.
  private resolveErrorText(
    errorBody: unknown,
    callback: (text: string) => void,
  ): void {
    if (errorBody instanceof Blob) {
      errorBody
        .text()
        .then(callback)
        .catch(() => callback(''))
    } else if (typeof errorBody === 'string') {
      callback(errorBody)
    } else {
      callback('')
    }
  }

  private showGenericPreviewError(): void {
    this.bannerService.showBanner(
      this.translate.instant('SERVICE_PLAN.STEP.REVIEW.PREVIEW_ERROR_BANNER'),
      'error',
      30000,
    )
  }

  private revokePdfBlobUrl(): void {
    if (this.pdfObjectUrl) {
      URL.revokeObjectURL(this.pdfObjectUrl)
      this.pdfObjectUrl = null
    }
  }

  // AC2 — primaryEmail + additionalEmails flattened into the single list ServicePlanSubmissionRequest
  // expects, skipping any blank additional row. Code review (2026-10-04): now mirrors
  // allRecipientsDisplay's own control.valid check — an invalid-looking address must never be
  // silently included in what is actually submitted, even though isSendEnabled already blocks Send
  // while any control is invalid (defence in depth, not a currently-reachable gap).
  private recipientEmails(): string[] {
    const emails: string[] = []
    if (this.primaryEmailControl.valid && this.primaryEmailControl.value) {
      emails.push(this.primaryEmailControl.value)
    }
    for (const control of this.additionalEmailsArray.controls) {
      if (control.valid && control.value) {
        emails.push(control.value)
      }
    }
    return emails
  }

  // AC3 — polls until either all four timestamps are non-null (full success) or failureReason
  // appears, with a hard safety cap so an unexpectedly slow pipeline doesn't poll forever on an
  // open tab.
  private startPolling(): void {
    this.pollSubscription?.unsubscribe()
    this.pollCount = 0
    this.isPollingCapped = false

    this.pollSubscription = interval(POLL_INTERVAL_MS)
      .pipe(
        switchMap(() => this.submissionService.GetStatus(this.submissionId!)),
      )
      .subscribe({
        next: (status) => {
          this.status = status
          this.pollCount++

          if (status.failureReason || this.isOutcomeFullyComplete(status)) {
            this.pollSubscription?.unsubscribe()
            this.cd.markForCheck()
            return
          }

          if (this.pollCount >= MAX_POLL_COUNT) {
            this.isPollingCapped = true
            this.pollSubscription?.unsubscribe()
          }
          // Each poll tick is its own independent zone macrotask, well after any DOM event that
          // might otherwise have re-dirtied ServicePlanStepperComponent's OnPush view — without this,
          // every row in the outcome box stays frozen on its very first rendered state forever.
          this.cd.markForCheck()
        },
        // Code review (2026-10-04): a transient HTTP error previously stopped polling completely
        // silently — visually indistinguishable from "still pending." Now shows a banner once and
        // reuses the existing capped/Check-again affordance as the recovery path, rather than
        // inventing a new UI state for what is operationally the same "manual resume" need.
        error: () => {
          this.pollSubscription?.unsubscribe()
          this.isPollingCapped = true
          this.bannerService.showBanner(
            this.translate.instant(
              'SERVICE_PLAN.STEP.REVIEW.POLLING_ERROR_BANNER',
            ),
            'error',
            30000,
          )
          this.cd.markForCheck()
        },
      })
  }

  // Task 4 — "all four timestamps are non-null," i.e. all of ARCH2's own four per-step
  // timestamps, not just the three rendered as their own outcome row (pdfGeneratedOn has no row
  // of its own — see outcomeRows' comment — but it is still part of the full-success condition).
  private isOutcomeFullyComplete(status: ServicePlanStatusDto): boolean {
    return (
      !!status.pdfGeneratedOn &&
      !!status.emailSentOn &&
      !!status.pdfAttachedOn &&
      !!status.noteCreatedOn
    )
  }

  private outcomeRowStatus(
    timestamp: string | null,
    failed: boolean,
  ): OutcomeRowStatus {
    if (timestamp) {
      return 'done'
    }
    return failed ? 'failed' : 'pending'
  }
}
